import { useState, useCallback } from 'react';
import { useLang } from '../../../hooks/useLang';
import { useReadProgress } from '../../../hooks/useReadProgress';

const SECTIONS = [
  { id: 'rwa-future',      zh: 'RWA未来愿景',    en: 'RWA Future Vision',    icon: 'ri-rocket-line',        color: '#A78BFA' },
  { id: 'rwa-timeline',    zh: '发展历程',        en: 'Timeline',             icon: 'ri-history-line',       color: '#F59E0B' },
  { id: 'site-map',        zh: '站点地图',        en: 'Site Map',             icon: 'ri-map-2-line',         color: '#60A5FA' },
  { id: 'rwa-entry',       zh: 'RWA入门',         en: 'RWA Basics',           icon: 'ri-seedling-line',      color: '#34D399' },
  { id: 'policy',          zh: '政策法规',        en: 'Policy',               icon: 'ri-government-line',    color: '#C084FC' },
  { id: 'cases',           zh: '案例库',          en: 'Case Library',         icon: 'ri-folder-chart-line',  color: '#F59E0B' },
  { id: 'hk-institutions', zh: '香港机构',        en: 'HK Institutions',      icon: 'ri-building-2-line',    color: '#FB923C' },
  { id: 'landing',         zh: '落地SOP',         en: 'Landing SOP',          icon: 'ri-route-line',         color: '#F472B6' },
  { id: 'news',            zh: '资讯动态',        en: 'News',                 icon: 'ri-news-line',          color: '#34D399' },
  { id: 'glossary',        zh: '名词百科',        en: 'Glossary',             icon: 'ri-book-2-line',        color: '#F59E0B' },
  { id: 'blockchain',      zh: '区块链技术',      en: 'Blockchain Tech',      icon: 'ri-links-line',         color: '#60A5FA' },
  { id: 'about',           zh: '关于我们',        en: 'About',                icon: 'ri-information-line',   color: '#A78BFA' },
];

const SECTION_IDS = SECTIONS.map((s) => s.id);

export default function ReadProgressWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const { viewed, viewedCount, totalCount, progressPct, resetProgress } = useReadProgress(SECTION_IDS);

  const handleScrollTo = useCallback((id: string) => {
    const el = document.getElementById(id) ?? document.querySelector(`[data-section-id="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setOpen(false);
    }
  }, []);

  return (
    <div className="fixed bottom-24 right-5 z-40 flex flex-col items-end gap-2">
      {/* Panel */}
      {open && (
        <div
          className="w-72 rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(15,0,48,0.97)',
            border: '1px solid rgba(167,139,250,0.25)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/8">
            <div className="flex items-center gap-2">
              <i className="ri-bookmark-3-line text-[#A78BFA] text-sm"></i>
              <span className="text-white font-bold text-sm">{t('阅读进度', 'Reading Progress')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#A78BFA] text-xs font-bold">{viewedCount}/{totalCount}</span>
              <button
                onClick={resetProgress}
                title={t('重置进度', 'Reset progress')}
                className="w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60 cursor-pointer transition-colors"
              >
                <i className="ri-refresh-line text-xs"></i>
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/40 text-xs">{t('已浏览板块', 'Sections viewed')}</span>
              <span className="text-[#A78BFA] text-xs font-bold">{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #7C3AED, #F59E0B)' }}
              />
            </div>
          </div>

          {/* Section list */}
          <div className="px-3 pb-3 max-h-72 overflow-auto" style={{ scrollbarWidth: 'thin' }}>
            {SECTIONS.map((sec) => {
              const isViewed = !!viewed[sec.id];
              return (
                <button
                  key={sec.id}
                  onClick={() => handleScrollTo(sec.id)}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/6 transition-all cursor-pointer group text-left"
                >
                  {/* Status dot */}
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full shrink-0 transition-all"
                    style={{
                      background: isViewed ? `${sec.color}25` : 'rgba(255,255,255,0.06)',
                      border: isViewed ? `1px solid ${sec.color}50` : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {isViewed ? (
                      <i className="ri-check-line text-xs" style={{ color: sec.color }}></i>
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0"
                    style={{ background: `${sec.color}15` }}
                  >
                    <i className={`${sec.icon} text-xs`} style={{ color: sec.color }}></i>
                  </div>

                  {/* Label */}
                  <span
                    className="text-xs font-medium flex-1 transition-colors group-hover:text-white"
                    style={{ color: isViewed ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.35)' }}
                  >
                    {lang === 'zh' ? sec.zh : sec.en}
                  </span>

                  {/* Jump arrow */}
                  <i className="ri-arrow-right-up-line text-xs text-white/20 group-hover:text-white/50 transition-colors shrink-0"></i>
                </button>
              );
            })}
          </div>

          {/* Footer tip */}
          <div className="px-4 py-2.5 border-t border-white/6 text-center">
            <p className="text-white/25 text-xs">
              {t('点击板块名称可快速跳转', 'Click section name to jump')}
            </p>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-11 h-11 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110"
        style={{
          background: open
            ? 'linear-gradient(135deg, #7C3AED, #4C1D95)'
            : 'linear-gradient(135deg, #1A0050, #2E1065)',
          border: '1px solid rgba(167,139,250,0.4)',
          boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
        }}
        title={t('阅读进度', 'Reading Progress')}
      >
        <i className={`${open ? 'ri-close-line' : 'ri-bookmark-3-line'} text-[#A78BFA] text-lg`}></i>

        {/* Progress ring */}
        <svg
          className="absolute inset-0 w-full h-full -rotate-90"
          viewBox="0 0 44 44"
          style={{ pointerEvents: 'none' }}
        >
          <circle cx="22" cy="22" r="19" fill="none" stroke="rgba(167,139,250,0.15)" strokeWidth="2" />
          <circle
            cx="22" cy="22" r="19"
            fill="none"
            stroke="url(#prog-grad)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 19}`}
            strokeDashoffset={`${2 * Math.PI * 19 * (1 - progressPct / 100)}`}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
          <defs>
            <linearGradient id="prog-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7C3AED" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
          </defs>
        </svg>

        {/* Badge */}
        {viewedCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full text-white font-black"
            style={{ fontSize: '9px', background: '#F59E0B' }}
          >
            {viewedCount}
          </span>
        )}
      </button>
    </div>
  );
}